import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface GenreColor {
  name: string;
  color: string;
}

interface GenreColorsMap {
  [genreName: string]: string;
}

/* -----------------------------------------------------------------
   Caching and listener infrastructure
----------------------------------------------------------------- */
let cachedArtistGenres: GenreColor[] | null = null;
let cachedEventGenres: GenreColor[] | null = null;

let fetchingArtist = false;
let fetchingEvent = false;

const artistListeners = new Set<() => void>();
const eventListeners = new Set<() => void>();

/* -----------------------------------------------------------------
   Data fetchers
----------------------------------------------------------------- */
async function fetchArtistGenres() {
  // Prevent duplicate fetches
  if (cachedArtistGenres || fetchingArtist) return;
  fetchingArtist = true;

  try {
    const { data, error } = await supabase
      .from('artist_genres')
      .select('name, color');

    if (error) {
      console.error('Error fetching artist genres:', error);
      cachedArtistGenres = [];
    } else {
      cachedArtistGenres = data ?? [];
    }

    // Notify all components that are listening
    artistListeners.forEach((fn) => fn());
  } catch (err) {
    console.error('Unexpected error while fetching artist genres:', err);
    cachedArtistGenres = [];
    // Still notify listeners so UI can fallback gracefully
    artistListeners.forEach((fn) => fn());
  } finally {
    fetchingArtist = false;
  }
}

async function fetchEventGenres() {
  if (cachedEventGenres || fetchingEvent) return;
  fetchingEvent = true;

  try {
    const { data, error } = await supabase
      .from('event_genres')
      .select('name, color');

    if (error) {
      console.error('Error fetching event genres:', error);
      cachedEventGenres = [];
    } else {
      cachedEventGenres = data ?? [];
    }

    eventListeners.forEach((fn) => fn());
  } catch (err) {
    console.error('Unexpected error while fetching event genres:', err);
    cachedEventGenres = [];
    eventListeners.forEach((fn) => fn());
  } finally {
    fetchingEvent = false;
  }
}

/* -----------------------------------------------------------------
   Helper: build a lookup map from the cached array
----------------------------------------------------------------- */
function buildMap(genres: GenreColor[] | null): GenreColorsMap {
  const map: GenreColorsMap = {};
  if (!genres) return map;

  genres.forEach((g) => {
    // Normalise the key to lower‑case for case‑insensitive lookup
    map[g.name.toLowerCase()] = g.color;
  });
  return map;
}

/* -----------------------------------------------------------------
   Public API: colour lookup
----------------------------------------------------------------- */
export function findGenreColor(
  genreString: string,
  genreMap: GenreColorsMap,
  fallback = '#888888',
): string {
  const lower = genreString.toLowerCase();

  // Exact match
  if (genreMap[lower]) return genreMap[lower];

  // Partial match: return the first colour whose key is contained in the string
  for (const key of Object.keys(genreMap)) {
    if (lower.includes(key)) return genreMap[key];
  }

  return fallback;
}

/* -----------------------------------------------------------------
   React hooks
----------------------------------------------------------------- */
export function useArtistGenreColors(): GenreColorsMap {
  const [map, setMap] = useState<GenreColorsMap>(() => buildMap(cachedArtistGenres));

  useEffect(() => {
    const update = () => setMap(buildMap(cachedArtistGenres));
    artistListeners.add(update);

    // Trigger the async fetch (will be a no‑op if already cached)
    fetchArtistGenres();

    return () => {
      artistListeners.delete(update);
    };
  }, []);

  return map;
}

export function useEventGenreColors(): GenreColorsMap {
  const [map, setMap] = useState<GenreColorsMap>(() => buildMap(cachedEventGenres));

  useEffect(() => {
    const update = () => setMap(buildMap(cachedEventGenres));
    eventListeners.add(update);

    fetchEventGenres();

    return () => {
      eventListeners.delete(update);
    };
  }, []);

  return map;
}

/* -----------------------------------------------------------------
   Combined hook used by pages – provides a convenient accessor
----------------------------------------------------------------- */
export function useGenreColors() {
  const artistMap = useArtistGenreColors();
  const eventMap = useEventGenreColors();

  // Merge both maps, event map can overwrite artist map if same key
  const combinedMap: GenreColorsMap = { ...artistMap, ...eventMap };

  const getGenreColor = (colorKey: string) => findGenreColor(colorKey, combinedMap);

  return { getGenreColor };
}